import EsfirusSelect from '@components/ui/Select/EsfirusSelect';
import { getAvailableCompanies, getInternalCompany } from '@helpers/companyData';
import Grid from '@mui/material/Grid';
import Modal from '@mui/material/Modal';
import { configurationService } from '@services/configApp';
import { customersService } from '@services/customers';
import useDebounce from '@services/hooks/useDebounce';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import EsfirusButton from '../../../../components/ui/Button/EsfirusButton';
import EsfirusTextInput from '../../../../components/ui/Text-input/EsfirusTextInput';
import './ModalNewClient.scss';

interface Client {
  unique_id: string;
  customer_id: string;
  business_name: string;
  company_id: number;
  vat_number: string;
}

interface ModalNewClientProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  selectClient: (client: Client) => Promise<void>;
  onlyInternalCompany?: boolean;
}

function ModalNewClient({ open, setOpen, selectClient, onlyInternalCompany = false }: ModalNewClientProps) {
  const empresas = useSelector((state: any) => state.empresas.list);
  const { filtroObraShowEmpresa } = configurationService.getConfigBuscadorObra();

  const [filterCustomerId, setFilterCustomerId] = useState('');
  const [filterBusinessName, setFilterBusinessName] = useState('');
  const [filterCIF, setFilterCIF] = useState('');
  const [selectedEmpresa, setSelectedEmpresa] = useState<any>(getInternalCompany());

  const [clientsList, setClientsList] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [currentClient, setCurrentClient] = useState<Client | null>(null);

  const inputRefs = useRef<{ [key: string]: HTMLInputElement }>({});

  const timeDebounce = 500;
  const debouncedCustomerId = useDebounce(filterCustomerId, timeDebounce);
  const debouncedBusinessName = useDebounce(filterBusinessName, timeDebounce);
  const debouncedCIFName = useDebounce(filterCIF, timeDebounce);
  const debouncedEmpresa = useDebounce(selectedEmpresa, timeDebounce);

  const empresasDisponibles = useMemo(
    () => onlyInternalCompany ? [getInternalCompany()] : getAvailableCompanies(true),
    [onlyInternalCompany]
  );

  const empresasOptions = useMemo(() => {
    return empresas
      .filter((e: any) => empresasDisponibles === '*' || (empresasDisponibles as any[]).includes(e.Empresa))
      .map((e: any) => ({ label: e.Nombre, value: e.Empresa }));
  }, [empresas, empresasDisponibles]);
  
  const fetchClients = async () => {
    setLoadingClients(true);
    try {
      const response = await customersService.getFilteredCustomers({
        customerId: debouncedCustomerId,
        businessName: debouncedBusinessName,
        companyId: debouncedEmpresa || undefined,
        vatNumber: debouncedCIFName,
        customerAmbit: empresas.find((e: any) => e.Empresa == debouncedEmpresa)?.Ambito,
      });

      const raw: Client[] = response.data.data || [];
      const seen = new Set();
      setClientsList(raw.filter(c => {
        if (seen.has(c.unique_id)) return false;
        seen.add(c.unique_id);
        return true;
      }));
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClientsList([]);
    }
    setLoadingClients(false);
  };

  useEffect(() => {
    if (open) fetchClients();
  }, [
    debouncedCustomerId,
    debouncedBusinessName,
    debouncedEmpresa,
    open,
    debouncedCIFName,
  ]);

  const acceptClose = async () => {
    if (currentClient) await selectClient(currentClient);
    setOpen(false);
  };

  return (
    <Modal open={open} onClose={() => setOpen(false)}>
      <div className="new-client-modal">
        <div className="header">
          <h5 className="text-center">Selecciona el cliente</h5>
        </div>

        <div className="body">
          <p className="text-center-mobile">
            Filtra el listado de clientes mediante los siguientes campos:
          </p>

          <Grid container spacing={4}>
            <Grid item md={6} xs={12}>
              <label>Id del Cliente</label>
              <EsfirusTextInput
                ref={(el: HTMLInputElement) =>
                  (inputRefs.current['customerId'] = el)
                }
                value={filterCustomerId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFilterCustomerId(e.target.value)
                }
                placeholder="Id del Cliente"
                fullWidth
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <label>Nombre</label>
              <EsfirusTextInput
                ref={(el: HTMLInputElement) =>
                  (inputRefs.current['businessName'] = el)
                }
                value={filterBusinessName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFilterBusinessName(e.target.value)
                }
                placeholder="Nombre"
                fullWidth
              />
            </Grid>
            {filtroObraShowEmpresa && (
              <Grid item md={6} xs={12}>
                <label>Empresa</label>
                <EsfirusSelect
                  options={empresasOptions}
                  value={selectedEmpresa}
                  change={setSelectedEmpresa}
                  disabled={loadingClients || onlyInternalCompany}
                  placeholder="Seleccionar empresa"
                />
              </Grid>
            )}

            <Grid item md={6} xs={12}>
              <label>CIF</label>
              <EsfirusTextInput
                ref={(el: HTMLInputElement) => (inputRefs.current['cif'] = el)}
                value={filterCIF}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFilterCIF(e.target.value)
                }
                placeholder="Nombre"
                fullWidth
              />
            </Grid>
          </Grid>

          <div className="client-container mt-20">
            <div className="scroll">
              {loadingClients ? (
                <div className="client-item">Cargando clientes...</div>
              ) : clientsList.length === 0 ? (
                <div className="client-item">
                  No existe ningún cliente con los filtros aplicados.
                </div>
              ) : (
                clientsList.map((client) => (
                  <div
                    key={client.unique_id}
                    className={`client-item${currentClient?.unique_id === client.unique_id ? ' selected' : ''}`}
                    onClick={() => setCurrentClient(client)}
                    onDoubleClick={async () => {
                      setCurrentClient(client);
                      await acceptClose();
                    }}
                  >
                    {client.customer_id} - {client.business_name} - {client.vat_number}
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="button-container">
            <EsfirusButton onClick={acceptClose} fullWidth color="secondary">
              Aceptar
            </EsfirusButton>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export { ModalNewClient };
